import { Button, Form, Input, Modal, message } from "antd";
import { CHANGE_MOBILE_STATE, CUSTOMER_PHONE_STATE } from "@zhihaoscm/meta";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { customerService } from "@/api/customerService";
import { customerWxService } from "@/api/customerWxService";
import { institutionApplyService } from "@/api/institutionApplyService";
import { isChainCloudTenant } from "@/api/legacy";
import { messageService } from "@/api/messageService";
import { Breadcrumb, DetailJson, type DetailJsonBlock } from "@/components/common";
import { refreshUserInfo } from "@/runtime/bootstrap";
import { AccountType, useAccountSession, useUserSession } from "@/stores/session";

interface PhoneForm { mobile: string; code: string }

/** 修改手机号弹窗：保持旧版校验、60 秒倒计时和认证分流规则。 */
export function ChangePhoneDialog({ open, state, onClose, onSuccess }: { open: boolean; state: number; onClose: () => void; onSuccess: () => Promise<void> }) {
    const [form] = Form.useForm<PhoneForm>();
    const [countdown, setCountdown] = useState(0);
    const [sending, setSending] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();
    const timerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

    /** 清理计时器，避免关闭页面后继续更新状态。 */
    const clearCountdown = () => { if (timerRef.current) clearInterval(timerRef.current); timerRef.current = undefined; };
    useEffect(() => clearCountdown, []);

    /** 验证手机号后发送换绑验证码。 */
    const sendCode = async () => {
        await form.validateFields(["mobile"]);
        setSending(true);
        try {
            await messageService.sendChangeMobile(form.getFieldValue("mobile"));
            setCountdown(60);
            clearCountdown();
            timerRef.current = setInterval(() => setCountdown(value => {
                if (value <= 1) { clearCountdown(); return 0; }
                return value - 1;
            }), 1000);
        } catch { void message.error("验证码发送失败，请稍后重试"); }
        finally { setSending(false); }
    };

    /** 校验验证码；仍需认证时进入契约锁流程，否则直接完成换绑。 */
    const submit = async () => {
        const values = await form.validateFields();
        setSubmitting(true);
        try {
            const nextState = await messageService.checkChangeMobile(values.mobile, values.code);
            if (nextState && nextState !== CUSTOMER_PHONE_STATE.UNREGISTERED.code) {
                onClose();
                navigate("/me/authentication/phone");
                return;
            }
            if (state === CHANGE_MOBILE_STATE.YES.code) {
                void message.success("换绑手机号成功");
                await onSuccess();
                form.resetFields();
                onClose();
            }
        } catch { void message.error("手机号换绑失败，请稍后重试"); }
        finally { setSubmitting(false); }
    };

    return <Modal className="change-phone-dialog" title="修改手机号" open={open} width={432} centered maskClosable={false} confirmLoading={submitting} okText="提交" cancelText="取消" onCancel={onClose} onOk={() => void submit()} destroyOnHidden>
        <Form form={form} labelCol={{ flex: "64px" }} wrapperCol={{ flex: 1 }} preserve={false}>
            <Form.Item name="mobile" label="手机号" rules={[{ required: true, message: "请输入手机号" }, { pattern: /^1[3456789]\d{9}$/, message: "请输入正确的手机号" }]}>
                <Input maxLength={11} showCount placeholder="请输入手机号" />
            </Form.Item>
            <Form.Item name="code" label="验证码" rules={[{ required: true, message: "请输入验证码" }]}>
                <ChangePhoneCodeInput sending={sending} countdown={countdown} onSend={() => void sendCode()} />
            </Form.Item>
        </Form>
    </Modal>;
}

/** 作为 Form.Item 的受控字段，避免把 value/onChange 错挂到布局 div。 */
function ChangePhoneCodeInput({ value, onChange, sending, countdown, onSend }: { value?: string; onChange?: (value: string) => void; sending: boolean; countdown: number; onSend: () => void }) {
    return <div className="change-phone-code"><Input value={value} onChange={event => onChange?.(event.target.value)} placeholder="请输入验证码" /><Button loading={sending} disabled={countdown > 0} onClick={onSend}>{countdown > 0 ? `${countdown}秒重新获取` : "发送验证码"}</Button></div>;
}

/** 个人信息页，字段和操作与 Vue jsonDetail 页面保持一致。 */
export function PersonalInfoPage() {
    const customer = useUserSession(state => state.userInfo.customer);
    const accountType = useAccountSession(state => state.accountType);
    const [phoneState, setPhoneState] = useState(CHANGE_MOBILE_STATE.NO.code);
    const [institutionName, setInstitutionName] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const canChangePhone = isChainCloudTenant() && accountType !== AccountType.SUB_ACCOUNT;

    /** 刷新资料后并行读取换绑状态及企业认证名称，单个附加接口失败不阻断页面。 */
    const load = async () => {
        try { await refreshUserInfo(); } catch { /* 启动阶段已有资料时允许降级展示。 */ }
        const latestCustomer = useUserSession.getState().userInfo.customer;
        if (canChangePhone && latestCustomer.id) {
            try { setPhoneState(Number(await customerService.getCustomerState(latestCustomer.id))); } catch { setPhoneState(CHANGE_MOBILE_STATE.NO.code); }
        }
        if (latestCustomer.applyState) {
            try {
                const record = await institutionApplyService.getLatestCertificationRecord();
                setInstitutionName(record?.institutionName || latestCustomer.institutionName || "");
            } catch { setInstitutionName(latestCustomer.institutionName || ""); }
        } else setInstitutionName(latestCustomer.institutionName || "");
    };

    useEffect(() => { void load(); }, []);
    useEffect(() => {
        const code = new URLSearchParams(location.search).get("code");
        if (!code || !customer.id) return;
        void customerWxService.bind({ customerId: customer.id, code }).then(result => {
            useUserSession.setState({ bindTime: result.bindTime ?? "", nickName: result.nickName ?? "" });
            void message.success("绑定成功");
            navigate(location.pathname, { replace: true });
        }).catch(() => void message.error("微信绑定失败，请稍后重试"));
    }, [customer.id, location.pathname, location.search, navigate]);

    /** 字段顺序、跨列和插槽内容完全对应旧 info/index.vue 的 DetailJsonBlock。 */
    const columns = useMemo<DetailJsonBlock<typeof customer>[]>(() => [{
        sectionTitle: <><Breadcrumb routes={[{ name: "账号管理" }, { name: "个人信息" }]} /><div className="personal-section-title">个人信息</div></>,
        list: [
            // 旧 DetailJson 按所在栅格列的最长标签计算宽度：四列依次为 56、56、84、56px。
            { key: "code", label: "账号", width: 56 },
            { label: "手机号", width: 56, render: () => <div className="personal-mobile"><span>{customer.mobile || "--"}</span>{canChangePhone && phoneState === CHANGE_MOBILE_STATE.YES.code ? <button type="button" className="iconfont icon-edit" aria-label="修改手机号" onClick={() => setDialogOpen(true)} /> : null}{canChangePhone && phoneState === CHANGE_MOBILE_STATE.PROCESSING.code ? <button type="button" className="personal-continue" onClick={() => navigate("/me/authentication/phone")}>继续换绑</button> : null}</div> },
            { label: "个人实名认证", width: 84, span: 2, render: () => <AuthValue authenticated={Boolean(customer.realName)} onAuth={() => navigate("/me/authentication")} /> },
            { key: "realName", label: "真实姓名", width: 56 },
            { key: "idNo", label: "身份证号", width: 56 },
            { label: "组织机构认证", width: 84, render: () => <AuthValue authenticated={Boolean(customer.applyState)} onAuth={() => navigate("/me/authentication")} /> },
            // 旧 Vue 始终保留企业名称字段；没有认证名称时插槽内容为空，不显示“--”。
            { label: "企业名称", width: 56, value: institutionName }
        ]
    }], [canChangePhone, customer, institutionName, navigate, phoneState]);

    return <section className="personal-info-page"><DetailJson columns={columns} data={customer} /><ChangePhoneDialog open={dialogOpen} state={phoneState} onClose={() => setDialogOpen(false)} onSuccess={load} /></section>;
}

function AuthValue({ authenticated, onAuth }: { authenticated: boolean; onAuth: () => void }) {
    return <div className="personal-auth-value"><span>{authenticated ? "已认证" : "未认证"}</span>{!authenticated ? <button type="button" onClick={onAuth}>去认证<i className="iconfont" /></button> : null}</div>;
}
